import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Table,
  TableHeader,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@design-system';
import { Check } from 'lucide-react';
import { quickEditFieldLabel } from '@shared';

export function IntakeChangeRequestChanges({
  request,
  showSummary = true,
  reviewButton = false,
}: {
  request: IntakeChangeRequestSummary;
  showSummary?: boolean;
  reviewButton?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const isPending = request.status === 'pending';
  const isRejected = request.status === 'rejected';
  const approvedDate = request.decidedDateTime
    ? new Date(request.decidedDateTime).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null;
  const visibleChanges = request.changes.filter(
    change => change.questionRef !== 'clientHasDifferentLtcZipCode',
  );

  return (
    <>
      <div
        className={
          showSummary
            ? 'text-muted-foreground flex flex-wrap items-center gap-x-2 gap-y-1 text-sm'
            : 'col-start-2'
        }
      >
        {showSummary ? (
          <>
            <Check
              className="h-4 w-4"
              aria-hidden="true"
            />
            <span>Last changes approved</span>
            {approvedDate ? <span>· {approvedDate}</span> : null}
          </>
        ) : null}
        <Button
          type="button"
          variant={reviewButton ? 'default' : 'link'}
          className={reviewButton ? '' : 'h-auto p-0'}
          aria-label={isRejected ? 'View rejected changes' : isPending && !reviewButton ? 'View proposed changes' : undefined}
          onClick={() => setOpen(true)}
        >
          {reviewButton ? 'Review changes' : 'View changes'}
        </Button>
      </div>
      <Dialog
        open={open}
        onOpenChange={setOpen}
      >
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {isRejected ? 'Rejected intake changes' : isPending ? 'Proposed intake changes' : 'Last approved intake changes'}
            </DialogTitle>
            <DialogDescription>
              {isRejected ? (
                'The client rejected these proposed changes. This request did not change their answers or plan.'
              ) : isPending ? (
                'These changes are awaiting client approval. Current answers and plan are unchanged.'
              ) : (
                <>
                  {approvedDate ? `Approved on ${approvedDate}. ` : ''}
                  These are the answers the client approved for this request.
                  {request.status === 'approved' ? ' These changes have not been applied.' : ''}
                  {request.status === 'superseded'
                    ? ' These changes were superseded by newer intake answers.'
                    : ''}
                  {request.status === 'applied' && request.quoteProjectionFailed
                    ? ' The answers were updated, but plan recalculation needs attention.'
                    : ''}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          {isRejected && (
            <div className="space-y-1 text-sm">
              <p className="font-medium">Client reason</p>
              <p className="whitespace-pre-wrap break-words text-muted-foreground">
                {request.rejectionReason || 'No reason provided.'}
              </p>
            </div>
          )}
          <div className="overflow-x-auto">
            <Table className="w-full table-fixed text-left text-sm">
              <TableHeader>
                <TableRow className="border-b">
                  <TableHead className="w-1/3 whitespace-normal p-3">Field</TableHead>
                  <TableHead className="w-1/3 whitespace-normal p-3">{isPending ? 'Current' : 'Previous'}</TableHead>
                  <TableHead className="w-1/3 whitespace-normal p-3">{isPending || isRejected ? 'Proposed' : 'Approved'}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleChanges.map(change => {
                  const hasClientValue = change.clientValueSet ?? change.clientValue !== null;
                  return (
                    <TableRow
                      key={change.questionRef}
                      className="border-b"
                    >
                      <TableCell
                        role="rowheader"
                        className="max-w-56 whitespace-normal break-words p-3 align-top font-medium"
                      >
                        {quickEditFieldLabel(change.questionRef)}
                      </TableCell>
                      <TableCell className="max-w-56 whitespace-normal break-words p-3 align-top">{displayValue(change.previousValue)}</TableCell>
                      <TableCell className="max-w-56 whitespace-normal break-words p-3 align-top">
                        {displayValue(!isRejected && hasClientValue ? change.clientValue : change.advisorValue)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={() => setOpen(false)}
          >
            Close
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}

function displayValue(value: string | number | boolean | null): string {
  if (value == null || value === '') return 'Not answered';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}
